import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import { fetch_needs_attention_daily, fetch_needs_attention_issues } from '../api';
import { fmt_count, month_label } from '../format';
import { ist_day } from '../shared/ist';
import {
  DAILY_ISSUES_PAGE_SIZE,
  DAILY_LOAD_FAILED_TEXT,
  daily_issue_list_init,
  daily_issue_next_request,
  daily_issue_page_applied,
  daily_issue_status,
  daily_view,
  issues_view
} from './adapters';
import {
  MAX_DAILY_LOOKBACK_DAYS,
  accept_day,
  can_shift_day,
  daily_params,
  issues_params,
  shift_day
} from './params';
import SessionCell from './session_cell';

/*
 * PROD-7828 R5b — the DAILY report: what is new or cleared since the previous digest post, plus the
 * anomaly threshold crossings and the worker outages of that window. The window is [day-1 10:00 IST,
 * day 10:00 IST) — the digest cadence, so the Slack post and this page describe the same 24 hours.
 * The MONTHLY report (the running state of each open month) is the other view of this tab.
 */
// The individual issues behind one DAILY line: Teacher, Session (SessionCell, same as the monthly
// list), ₹ at stake, Status. No issue-code column — Kuldeep: no internal codes in the UI.
const drilldown_columns = [
  {
    key: 'teacher_name',
    label: 'Teacher',
    render: (r) => (
      <div>
        <div>{r.teacher_name}</div>
        <TeacherSlugLine slug={r.teacher_slug} />
      </div>
    )
  },
  { key: 'session', label: 'Session', render: (r) => <SessionCell row={r} /> },
  {
    key: 'amount_at_stake',
    label: '₹ at stake',
    align: 'right',
    render: (r) => <span className="tp-strong">{r.amount_text}</span>
  },
  {
    key: 'status',
    label: 'Status',
    render: (r) => <StatusPill tone={r.status_tone}>{r.status_label}</StatusPill>
  }
];

// One line's drill-down: fetched from the same /needs_attention/issues endpoint the monthly list uses,
// filtered by issue_code, one of the line's pay_months at a time (daily_issue_next_request/_applied).
const DailyIssueDrilldown = ({ line, section_key, on_error }) => {
  const [state, setState] = useState(() => daily_issue_list_init(line.pay_months));
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);

  const load_more = useCallback(() => {
    const request = daily_issue_next_request(state);
    if (!request || loading) return;
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setFailed(false);
    fetch_needs_attention_issues(
      issues_params(
        {
          pay_month: request.pay_month,
          issue_code: line.issue_code,
          status: daily_issue_status(section_key),
          sort: 'session_time',
          dir: 'asc'
        },
        DAILY_ISSUES_PAGE_SIZE,
        request.offset
      )
    )
      .then((data) => {
        if (mine !== seq.current) return;
        setState((s) => daily_issue_page_applied(s, request, issues_view(data)));
        setLoading(false);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        on_error(e, 'Failed to load these issues.');
        setFailed(true);
        setLoading(false);
      });
  }, [state, loading, line.issue_code, line.pay_months, section_key, on_error]);

  // Load the first page on mount; a re-mount (line collapsed and reopened) starts fresh.
  useEffect(() => {
    load_more();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="tp-na-drilldown">
      <DataTable
        columns={drilldown_columns}
        rows={state.rows}
        rowKey={(r) => r.key}
        emptyText={loading ? '' : 'No issues loaded.'}
        cardTitle={(r) => r.teacher_name}
      />
      {loading ? <CustomLoading /> : null}
      {failed ? <LoadFailed text="Failed to load these issues." on_retry={load_more} /> : null}
      {!loading && !failed && !state.done ? (
        <div className="flex items-center justify-center mt-3">
          <button type="button" className="tp-btn-out" onClick={load_more}>
            Show more
          </button>
        </div>
      ) : null}
    </div>
  );
};

// One table per DAILY section (New/Cleared/Thresholds), each with its own independent sort state and
// set of expanded lines (chevron -> DailyIssueDrilldown).
const DailySection = ({ section, on_error }) => {
  const [sort, setSort] = useState(null);
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));
  const [expanded, setExpanded] = useState({});
  const toggle_line = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const line_columns = [
    {
      key: 'expand',
      label: '',
      hideInCard: true,
      render: (l) => (
        <button
          type="button"
          className="tp-th-sort"
          aria-expanded={!!expanded[l.key]}
          aria-label={`${expanded[l.key] ? 'Collapse' : 'Expand'} ${l.label}`}
          onClick={() => toggle_line(l.key)}
        >
          <span aria-hidden="true">{expanded[l.key] ? '▾' : '▸'}</span>
        </button>
      )
    },
    {
      key: 'label',
      label: 'Issue',
      sort_key: 'label',
      sort_type: 'text',
      // Kuldeep: no internal issue codes in the UI; the code stays in the CSV and, in the monthly
      // list, behind "Engine detail".
      render: (l) => (
        <button
          type="button"
          className="tp-link"
          aria-expanded={!!expanded[l.key]}
          onClick={() => toggle_line(l.key)}
        >
          <StatusPill tone={l.tier_tone}>{l.tier_label}</StatusPill> {l.label}
        </button>
      )
    },
    {
      key: 'count',
      label: 'Issues',
      align: 'right',
      sort_key: 'count',
      sort_type: 'number',
      render: (l) => fmt_count(l.count)
    },
    {
      key: 'teachers',
      label: 'Teachers',
      align: 'right',
      sort_key: 'teachers',
      sort_type: 'number',
      render: (l) => fmt_count(l.teachers)
    },
    {
      key: 'amount_at_stake',
      label: '₹ at stake',
      align: 'right',
      sort_key: 'amount_at_stake',
      sort_type: 'number',
      render: (l) => <span className="tp-strong">{l.amount_text}</span>
    },
    { key: 'months_text', label: 'Months', sort_key: 'months_text', sort_type: 'text' }
  ];

  const rows = sort_rows(section.lines, line_columns, sort);
  return (
    <div className="tp-card">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title">{section.title}</span>
      </div>
      <DataTable
        columns={line_columns}
        rows={rows}
        rowKey={(l) => l.key}
        sort={sort}
        on_sort={on_sort}
        emptyText={section.empty_text}
        cardTitle={(l) => l.label}
      />
      {rows
        .filter((l) => expanded[l.key])
        .map((l) => (
          <div className="tp-card" key={l.key}>
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">{l.label}</span>
            </div>
            <DailyIssueDrilldown line={l} section_key={section.key} on_error={on_error} />
          </div>
        ))}
    </div>
  );
};

const system_now = () => new Date();

// can_open_month: whether the shell lists the link's month; an unlisted month gets no button.
const DailyReport = ({
  day,
  now = system_now,
  on_day,
  on_error,
  can_open_month,
  on_open_monthly
}) => {
  const today = ist_day(now());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => daily_view(null, today));
  const [load_failed, setLoadFailed] = useState(false);
  const request_seq = useRef(0);

  const load = useCallback(
    (next_day) => {
      request_seq.current += 1;
      const seq = request_seq.current;
      setLoading(true);
      setLoadFailed(false);
      fetch_needs_attention_daily(daily_params(next_day))
        .then((data) => {
          if (seq !== request_seq.current) return;
          setView(daily_view(data, today));
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          on_error(e, 'Failed to load the daily report.');
          setView(daily_view(null, today));
          setLoadFailed(true);
          setLoading(false);
        });
    },
    [on_error, today]
  );

  useEffect(() => {
    load(day);
  }, [day, load]);

  // A day outside the served window (future, or older than 90 days) is never requested — the backend
  // would answer BAD_DAY and Ops would see an error instead of an empty day.
  const pick_day = (next) => {
    if (!accept_day(next, today)) return;
    on_day(next);
  };

  // After a failed load the view is empty; the picker stays on the day that was asked for.
  const shown_day = view.day || day || today;
  const prev_off = !can_shift_day(shown_day, -1, today);
  const next_off = !can_shift_day(shown_day, 1, today);

  return (
    <div>
      <div className="tp-frow">
        {/* aria-disabled, not disabled: an arrow that reaches the edge keeps focus (CD-114). */}
        <button
          type="button"
          className="tp-btn-ghost"
          onClick={prev_off ? undefined : () => pick_day(shift_day(shown_day, -1))}
          aria-disabled={prev_off ? 'true' : undefined}
        >
          ← Previous day
        </button>
        <input
          className="tp-input"
          type="date"
          value={shown_day}
          max={today}
          min={shift_day(today, -MAX_DAILY_LOOKBACK_DAYS)}
          onChange={(e) => pick_day(e.target.value)}
          aria-label="Report day (IST)"
        />
        <button
          type="button"
          className="tp-btn-ghost"
          onClick={next_off ? undefined : () => pick_day(shift_day(shown_day, 1))}
          aria-disabled={next_off ? 'true' : undefined}
        >
          Next day →
        </button>
        <div className="tp-frow-right">
          {view.monthly_target.pay_month && can_open_month(view.monthly_target.pay_month) ? (
            <button
              type="button"
              className="tp-link"
              onClick={() => on_open_monthly(view.monthly_target)}
            >
              Open the {month_label(view.monthly_target.pay_month)} monthly report
            </button>
          ) : null}
        </div>
      </div>

      {loading ? <CustomLoading /> : null}
      {!loading && load_failed ? (
        <LoadFailed text={DAILY_LOAD_FAILED_TEXT} on_retry={() => load(day)} />
      ) : null}
      {!loading && !load_failed ? (
        <div>
          <div className={`tp-banner ${view.all_clear ? 'tp-banner-ok' : 'tp-banner-info'}`}>
            <div className="tp-na-headline">
              {view.all_clear ? 'All clear — nothing moved in this window.' : view.totals_text}
            </div>
            <div>{view.window_text}</div>
          </div>

          {view.sections.map((s) => (
            <DailySection key={s.key} section={s} on_error={on_error} />
          ))}

          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Jobs</span>
            </div>
            {view.job_events.length ? (
              view.job_events.map((j) => (
                <div key={j.key} className="tp-na-blocker">
                  <StatusPill tone={j.tone}>{j.event_label}</StatusPill> {j.text} · {j.at}
                </div>
              ))
            ) : (
              <div className="tp-empty">No worker outage or recovery in this window.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DailyReport;
